import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';
import PortfolioGallery from './PortfolioGallery';
import {
  detectVideoType, linkThumbnailUrl, captureVideoThumbnail, uploadWithProgress, storagePathFromPublicUrl,
} from '../utils/videoPortfolio';

const NAVY      = '#0D1B2A';
const NAVY_MID  = '#132233';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

const inp = {
  width: '100%', padding: '9px 12px', fontSize: '0.88rem', boxSizing: 'border-box',
  border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px',
  color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)', fontFamily: FONT_BODY,
};
const lbl = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '5px' };
const fld = { marginBottom: '1rem' };
const EMPTY_FORM = { title: '', description: '', videoUrl: '' };
const ACCEPTED_VIDEO = 'video/mp4,video/quicktime,video/webm';

// Management UI for an organization-level video portfolio -- the worker portfolio's add/upload/
// delete flow (WorkerDashboard "Portfolio"), parameterized by table/owner/bucket so the employer
// Company Profile tab and the union dashboard share one implementation:
//   employer: table="employer_portfolio" ownerColumn="employer_id" bucket="employer-videos"
//   union:    table="union_portfolio"    ownerColumn="union_id"    bucket="union-videos"
// Files live under `${ownerId}/${itemKey}/` so each org's objects are grouped by owner.
export default function PortfolioManager({ table, ownerColumn, ownerId, bucket, subtitle }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const [items, setItems]         = useState([]);
  const [loaded, setLoaded]       = useState(false);
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [linkMode, setLinkMode]   = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [progress, setProgress]   = useState(0);
  const [stage, setStage]         = useState(''); // '' | 'uploading' | 'finishing'
  const [error, setError]         = useState('');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (!ownerId) return;
    let cancelled = false;
    supabase.from(table).select('*').eq(ownerColumn, ownerId).order('created_at', { ascending: false })
      .then(({ data }) => { if (!cancelled) { setItems(data || []); setLoaded(true); } });
    return () => { cancelled = true; };
  }, [table, ownerColumn, ownerId]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setVideoFile(null);
    setLinkMode(false);
    setProgress(0);
    setStage('');
    setError('');
  };

  const handleSave = async () => {
    setError('');
    const title = form.title.trim();
    if (!title) { setError(t('portfolio.titleRequired')); return; }

    let videoUrl = null;
    let videoType = null;
    let thumbnailUrl = null;
    const itemKey = window.crypto.randomUUID();

    if (linkMode) {
      videoUrl = form.videoUrl.trim();
      videoType = detectVideoType(videoUrl);
      // Only links the gallery can actually play -- the worker flow stored arbitrary 'link'
      // URLs that then rendered as nothing.
      if (!videoUrl || (videoType !== 'youtube' && videoType !== 'vimeo')) { setError(t('portfolio.invalidLink')); return; }
      thumbnailUrl = linkThumbnailUrl(videoUrl);
      setSaving(true);
    } else {
      if (!videoFile) { setError(t('portfolio.videoRequired')); return; }
      setSaving(true);
      setStage('uploading');
      setProgress(0);
      const { data: { session } } = await supabase.auth.getSession();
      const ext = (videoFile.name.split('.').pop() || 'mp4').toLowerCase().replace(/[^a-z0-9]/g, '') || 'mp4';
      const videoPath = `${ownerId}/${itemKey}/video.${ext}`;
      const { error: uploadErr } = await uploadWithProgress({
        supabaseUrl: process.env.REACT_APP_SUPABASE_URL,
        anonKey: process.env.REACT_APP_SUPABASE_ANON_KEY,
        accessToken: session?.access_token,
        bucket,
        path: videoPath,
        file: videoFile,
        contentType: videoFile.type || 'video/mp4',
        onProgress: setProgress,
      });
      if (uploadErr) {
        console.error('[portfolio-upload] upload failed:', uploadErr);
        setError(uploadErr.status === 413 ? t('portfolio.fileTooLarge') : t('portfolio.uploadFailed'));
        setSaving(false);
        setStage('');
        return;
      }
      videoUrl = supabase.storage.from(bucket).getPublicUrl(videoPath).data.publicUrl;
      videoType = 'upload';

      // Thumbnail is a nice-to-have: any failure here still saves the video itself.
      setStage('finishing');
      const thumbBlob = await captureVideoThumbnail(videoFile);
      if (thumbBlob) {
        const thumbPath = `${ownerId}/${itemKey}/thumbnail.jpg`;
        const { error: thumbErr } = await supabase.storage.from(bucket).upload(thumbPath, thumbBlob, { contentType: 'image/jpeg', upsert: true });
        if (!thumbErr) thumbnailUrl = supabase.storage.from(bucket).getPublicUrl(thumbPath).data.publicUrl;
      }
    }

    const { data, error: insertErr } = await supabase
      .from(table)
      .insert({
        [ownerColumn]: ownerId,
        title,
        description: form.description.trim() || null,
        video_url:     videoUrl,
        video_type:    videoType,
        thumbnail_url: thumbnailUrl,
      })
      .select()
      .single();

    setSaving(false);
    setStage('');
    if (insertErr || !data) {
      console.error('[portfolio-insert] failed:', insertErr);
      setError(t('portfolio.saveFailed'));
      return;
    }
    setItems(prev => [data, ...prev]);
    resetForm();
    setShowForm(false);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(t('portfolio.deleteConfirm', { title: item.title || '' }))) return;
    setDeletingId(item.id);
    const { error: delErr } = await supabase.from(table).delete().eq('id', item.id);
    setDeletingId(null);
    if (delErr) { setError(t('portfolio.deleteFailed')); return; }
    setItems(prev => prev.filter(p => p.id !== item.id));
    // Best-effort storage cleanup so removed videos don't linger publicly in the bucket.
    const paths = [storagePathFromPublicUrl(item.video_url, bucket), storagePathFromPublicUrl(item.thumbnail_url, bucket)].filter(Boolean);
    if (paths.length) supabase.storage.from(bucket).remove(paths).then(() => {});
  };

  const toggleBtn = (active) => ({
    flex: 1, padding: '8px', borderRadius: '7px', fontFamily: FONT_BODY, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer',
    border: `1.5px solid ${active ? AMBER : 'rgba(255,255,255,0.15)'}`,
    background: active ? 'rgba(245,166,35,0.1)' : 'transparent',
    color: active ? AMBER : 'rgba(255,255,255,0.45)',
  });

  return (
    <section data-portfolio-manager style={{ fontFamily: FONT_BODY }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: subtitle ? '4px' : '16px', flexWrap: 'wrap' }}>
        <h3 style={{ fontFamily: FONT_HEAD, fontSize: '1.3rem', color: WHITE, letterSpacing: '0.05em' }}>{t('portfolio.heading')}</h3>
        <button
          onClick={() => { resetForm(); setShowForm(v => !v); }}
          disabled={saving}
          style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.82rem', padding: '7px 16px', borderRadius: '7px', cursor: 'pointer', fontFamily: FONT_BODY }}
        >
          {showForm ? t('common.cancel') : t('portfolio.addVideo')}
        </button>
      </div>
      {subtitle && <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.45)', marginBottom: '16px', lineHeight: 1.5 }}>{subtitle}</p>}

      {showForm && (
        <div data-portfolio-form style={{ background: NAVY_MID, borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
          <div style={fld}>
            <label htmlFor="portfolio-title" style={lbl}>{t('portfolio.titleLabel')}</label>
            <input id="portfolio-title" style={inp} maxLength={120} placeholder={t('portfolio.titlePlaceholder')} value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} />
          </div>
          <div style={fld}>
            <label htmlFor="portfolio-description" style={lbl}>{t('portfolio.descriptionLabel')}</label>
            <textarea id="portfolio-description" style={{ ...inp, minHeight: '72px', resize: 'vertical' }} placeholder={t('portfolio.descriptionPlaceholder')} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>

          {/* Toggle: upload vs link -- same two modes as the worker portfolio */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <button type="button" onClick={() => { setLinkMode(false); setForm(p => ({ ...p, videoUrl: '' })); setError(''); }} style={toggleBtn(!linkMode)} disabled={saving}>
              {t('portfolio.uploadToggle')}
            </button>
            <button type="button" onClick={() => { setLinkMode(true); setVideoFile(null); setError(''); }} style={toggleBtn(linkMode)} disabled={saving}>
              {t('portfolio.linkToggle')}
            </button>
          </div>

          {!linkMode && (
            <div style={fld}>
              <input
                ref={fileInputRef}
                data-portfolio-file
                type="file"
                accept={ACCEPTED_VIDEO}
                style={{ display: 'none' }}
                onChange={e => { setVideoFile(e.target.files?.[0] || null); setError(''); e.target.value = ''; }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={saving}
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.15)', color: WHITE, borderRadius: '8px', padding: '9px 16px', fontSize: '0.84rem', fontWeight: 600, cursor: 'pointer', fontFamily: FONT_BODY }}
                >{t('portfolio.chooseFile')}</button>
                {videoFile && (
                  <span style={{ fontSize: '0.82rem', color: AMBER, fontWeight: 500, overflowWrap: 'anywhere', minWidth: 0 }}>
                    {videoFile.name} · {(videoFile.size / (1024 * 1024)).toFixed(1)} MB
                  </span>
                )}
              </div>
              <p style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.38)', marginTop: '6px' }}>{t('portfolio.fileHint')}</p>
              {stage && (
                <div style={{ marginTop: '10px' }}>
                  <div
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={stage === 'finishing' ? 100 : progress}
                    style={{ height: '4px', borderRadius: '2px', background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}
                  >
                    <div style={{ height: '100%', width: `${stage === 'finishing' ? 100 : progress}%`, background: AMBER, transition: 'width 0.2s' }} />
                  </div>
                  <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', marginTop: '4px', display: 'block' }}>
                    {stage === 'finishing' ? t('portfolio.finishing') : t('portfolio.uploadingPercent', { percent: progress })}
                  </span>
                </div>
              )}
            </div>
          )}

          {linkMode && (
            <div style={fld}>
              <label htmlFor="portfolio-link" style={lbl}>{t('portfolio.linkLabel')}</label>
              <input id="portfolio-link" style={inp} inputMode="url" placeholder="https://www.youtube.com/watch?v=..." value={form.videoUrl} onChange={e => { setForm(p => ({ ...p, videoUrl: e.target.value })); setError(''); }} />
            </div>
          )}

          {error && <p role="alert" style={{ fontSize: '0.82rem', fontWeight: 600, color: '#F87171', marginBottom: '10px' }}>{error}</p>}

          <button
            onClick={handleSave}
            disabled={saving}
            style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.88rem', padding: '9px 20px', borderRadius: '8px', cursor: saving ? 'default' : 'pointer', fontFamily: FONT_BODY, opacity: saving ? 0.7 : 1 }}
          >
            {saving ? t('portfolio.saving') : t('portfolio.saveButton')}
          </button>
        </div>
      )}

      {!showForm && error && <p role="alert" style={{ fontSize: '0.82rem', fontWeight: 600, color: '#F87171', marginBottom: '10px' }}>{error}</p>}

      {!loaded ? null : items.length === 0 ? (
        <div style={{ background: NAVY_MID, borderRadius: '10px', padding: '28px 20px', textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '14px' }}>
          {t('portfolio.empty')}
        </div>
      ) : (
        <PortfolioGallery items={items} variant="dark" onDelete={handleDelete} deletingId={deletingId} />
      )}
    </section>
  );
}
